import type { TTranslate } from '@/interfaces';
import { computed, nextTick, ref, shallowRef, watch, type ComputedRef, type Ref, type ShallowRef } from 'vue';
import type { IRequiredActionIssue, IRequiredActionPrompt } from './requiredAction.types';

type TRequiredActionMatcher<TIssue> = (issue: TIssue) => boolean;

interface IRequiredActionGuidanceState<TIssue extends IRequiredActionIssue> {
  activeIssue: ShallowRef<TIssue | null>;
  announcement: Ref<string>;
  animationSequence: Ref<number>;
  attemptCount: Ref<number>;
  tooltipVisible: Ref<boolean>;
}

export interface IRequiredActionGuidance<TIssue extends IRequiredActionIssue> {
  announcement: Readonly<Ref<string>>;
  getPrompt: (matchesTarget: TRequiredActionMatcher<TIssue>) => IRequiredActionPrompt | null;
  interact: (matchesTarget: TRequiredActionMatcher<TIssue>) => boolean;
  interactWithAction: (action: TIssue['action']) => boolean;
  promptForAction: (action: TIssue['action']) => ComputedRef<IRequiredActionPrompt | null>;
  requestNext: () => Promise<TIssue | null>;
  reset: () => void;
  announce: (message: string) => Promise<void>;
}

const isSameIssue = <TIssue extends IRequiredActionIssue>(first: TIssue, second: TIssue): boolean =>
  first.action === second.action && first.code === second.code;

const createGuidanceState = <TIssue extends IRequiredActionIssue>(): IRequiredActionGuidanceState<TIssue> => ({
  // Vue cannot preserve a constrained generic through shallowRef's UnwrapRef overload.
  activeIssue: shallowRef(null) as ShallowRef<TIssue | null>,
  announcement: ref(''),
  animationSequence: ref(0),
  attemptCount: ref(0),
  tooltipVisible: ref(false),
});

const resetGuidanceState = <TIssue extends IRequiredActionIssue>(state: IRequiredActionGuidanceState<TIssue>): void => {
  state.activeIssue.value = null;
  state.announcement.value = '';
  state.attemptCount.value = 0;
  state.tooltipVisible.value = false;
};

const activateIssue = <TIssue extends IRequiredActionIssue>(
  state: IRequiredActionGuidanceState<TIssue>,
  issue: TIssue,
): void => {
  const repeatsActiveIssue = state.activeIssue.value ? isSameIssue(state.activeIssue.value, issue) : false;
  state.attemptCount.value = repeatsActiveIssue ? state.attemptCount.value + 1 : 1;
  state.activeIssue.value = issue;
  state.animationSequence.value += 1;
  state.tooltipVisible.value = state.attemptCount.value >= 2;
};

const resolvePrompt = <TIssue extends IRequiredActionIssue>(
  state: IRequiredActionGuidanceState<TIssue>,
  matchesTarget: TRequiredActionMatcher<TIssue>,
  translate: TTranslate,
): IRequiredActionPrompt | null => {
  const issue = state.activeIssue.value;
  if (!issue || !matchesTarget(issue)) return null;
  return {
    animationSequence: state.animationSequence.value,
    message: translate(issue.messageKey),
    showTooltip: state.tooltipVisible.value,
  };
};

const interactWithTarget = <TIssue extends IRequiredActionIssue>(
  state: IRequiredActionGuidanceState<TIssue>,
  matchesTarget: TRequiredActionMatcher<TIssue>,
): boolean => {
  const issue = state.activeIssue.value;
  if (!issue || !matchesTarget(issue)) return false;
  state.announcement.value = '';
  state.tooltipVisible.value = false;
  return true;
};

export const useRequiredActionGuidance = <TIssue extends IRequiredActionIssue>(
  nextIssue: ComputedRef<TIssue | null>,
  translate: TTranslate,
): IRequiredActionGuidance<TIssue> => {
  const state = createGuidanceState<TIssue>();

  const announce = async (message: string): Promise<void> => {
    state.announcement.value = '';
    await nextTick();
    state.announcement.value = message;
  };
  const reset = (): void => resetGuidanceState(state);

  const requestNext = async (): Promise<TIssue | null> => {
    const issue = nextIssue.value;
    if (!issue) return null;
    activateIssue(state, issue);
    await announce(translate(issue.messageKey));
    return issue;
  };
  const getPrompt = (matchesTarget: TRequiredActionMatcher<TIssue>): IRequiredActionPrompt | null =>
    resolvePrompt(state, matchesTarget, translate);
  const interact = (matchesTarget: TRequiredActionMatcher<TIssue>): boolean => interactWithTarget(state, matchesTarget);
  const interactWithAction = (action: TIssue['action']): boolean =>
    interactWithTarget(state, (issue) => issue.action === action);
  const promptForAction = (action: TIssue['action']): ComputedRef<IRequiredActionPrompt | null> =>
    computed(() => resolvePrompt(state, (issue) => issue.action === action, translate));

  watch(
    nextIssue,
    (issue) => {
      if (!state.activeIssue.value) return;
      if (!issue || !isSameIssue(issue, state.activeIssue.value)) reset();
    },
    { flush: 'sync' },
  );

  return {
    announcement: state.announcement,
    getPrompt,
    interact,
    interactWithAction,
    promptForAction,
    requestNext,
    reset,
    announce,
  };
};
